import React, {useCallback, useState} from 'react';
import {TransitionGroup} from 'react-transition-group';

import {withCssModule} from '@pb/kit-util-with-css-module';

import {
    Animation,
    DURATION_ENTER,
    DURATION_EXIT,
    HorizontalPositionElement,
    MAX_NOTIFICATION,
    VerticalPositionElement,
} from './constants';
import {PushNotificationWithContext} from './contexts/push-notification-with-context';
import {PushNotificationItem} from './push-notification-item/push-notification-item';
import styles from './push-notification.module.styl';

import type {
    AllConfig,
    AnchorOriginType,
    AnimationType,
    OnCloseAllNote,
    OnCloseNote,
    OnCreateNote,
} from './typedef';
import type {ComponentType, ReactNode} from 'react';

const classNames = withCssModule(styles);

export type PushNotificationProps = {
    /**
     * Maximum number of toasts visible at the same time. When a new notification
     * is created and the limit is exceeded, the oldest toast is auto-dismissed.
     * Pass `0` to disable the cap. Defaults to `MAX_NOTIFICATION` (3).
     */
    maxNote?: number,
    /**
     * Screen corner where the notification stack is anchored. Accepts an object
     * with `horizontal` (`"LEFT"` | `"RIGHT"`) and `vertical` (`"TOP"` | `"BOTTOM"`)
     * fields. Defaults to `{ vertical: "TOP", horizontal: "RIGHT" }`.
     */
    anchorOrigin?: AnchorOriginType,
    /**
     * Entrance/exit animation style. Use `Animation.SLIDE` (default) for toasts
     * that slide in from the anchor edge, or `Animation.FADE` for a cross-fade.
     */
    animation?: AnimationType,
    /**
     * Additional CSS class names applied to the fixed-position toast container.
     */
    className?: string,
    /**
     * The application subtree that consumes `usePushNotification`. The toast
     * overlay is rendered as a sibling to these children inside the context
     * provider, not as a portal.
     */
    children: ReactNode,
};

/**
 * Toast notification container that manages a stack of transient messages
 * triggered imperatively from anywhere in the wrapped subtree.
 *
 * Wrap the application (or a page section) with `PushNotification` and use the
 * `usePushNotification` hook (from the context) inside any descendant to create
 * or dismiss notifications programmatically. The container renders toasts into a
 * fixed-position `TransitionGroup` overlay and removes them after their exit
 * animation completes.
 *
 * ## Features
 * - Four anchor positions: top/bottom × left/right via `anchorOrigin`
 * - `maxNote` cap: the oldest toast is auto-dismissed when the limit is exceeded
 * - `animation`: `"SLIDE"` (default) or `"FADE"` entrance/exit transitions
 * - Deduplication: toasts with the same `id` are silently ignored even across
 *   rapid re-renders (checked both in state and in the live DOM)
 * - `onCloseNote` / `onCloseAllNote` return Promises that resolve after the
 *   exit animation duration, making it easy to chain follow-up actions
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/components-push-notification--documentation}
 */
export const PushNotification: ComponentType<PushNotificationProps> = (props: PushNotificationProps) => {
    const {
        maxNote = MAX_NOTIFICATION,
        anchorOrigin = {
            vertical: VerticalPositionElement.TOP,
            horizontal: HorizontalPositionElement.RIGHT,
        },
        animation = Animation.SLIDE,
        className = '',
        children,
    } = props;

    const [notes, setNotes] = useState<Array<AllConfig>>([]);

    const onCloseAllNote = useCallback<OnCloseAllNote>(() => new Promise(resolve => {
        setNotes([]);

        setTimeout(resolve, DURATION_EXIT);
    }), []);

    const onCloseNote = useCallback<OnCloseNote>(noteId => new Promise(resolve => {
        setNotes(prevState => prevState.filter(config => config.id !== noteId));

        setTimeout(resolve, DURATION_EXIT);
    }), []);

    const onCreateNote = useCallback<OnCreateNote>(config => new Promise(resolve => {
        setNotes(prevState => {
            const isInDom = !!document.getElementById(`push-notification-${config.id}`);
            const isNotUniqueId = prevState.some(item => item.id === config.id) || isInDom;

            if (isNotUniqueId) {
                return prevState;
            }

            return [
                ...prevState,
                {...config, onCloseNote, onCloseAllNote},
            ];
        });

        setTimeout(resolve, DURATION_ENTER);
    }), [onCloseNote, onCloseAllNote]);

    const containerClasses = classNames('push-notification', className, {
        'push-notification_bottom-left':
            anchorOrigin.vertical === VerticalPositionElement.BOTTOM &&
            anchorOrigin.horizontal === HorizontalPositionElement.LEFT,
        'push-notification_bottom-right':
            anchorOrigin.vertical === VerticalPositionElement.BOTTOM &&
            anchorOrigin.horizontal === HorizontalPositionElement.RIGHT,
        'push-notification_top-left':
            anchorOrigin.vertical === VerticalPositionElement.TOP &&
            anchorOrigin.horizontal === HorizontalPositionElement.LEFT,
        'push-notification_top-right':
            anchorOrigin.vertical === VerticalPositionElement.TOP &&
            anchorOrigin.horizontal === HorizontalPositionElement.RIGHT,
        'push-notification_slide': animation === Animation.SLIDE,
        'push-notification_fade': animation === Animation.FADE,
    });

    return (
        <PushNotificationWithContext
            onCreateNote={onCreateNote}
            onCloseNote={onCloseNote}
            onCloseAllNote={onCloseAllNote}>

            {children}

            <TransitionGroup className={containerClasses}>
                {notes.map((config, index) => (
                    <PushNotificationItem
                        key={config.id}
                        isNeedClosed={maxNote !== 0 && notes.length > maxNote && index === 0}
                        {...config}
                    />
                ))}
            </TransitionGroup>
        </PushNotificationWithContext>
    );
};

PushNotification.displayName = 'PushNotification';
